function readSectionValue(section, key) {
  if (!(section && typeof section === 'object')) return ''
  return String(section[key] || '').trim()
}

function readSectionContent(section) {
  if (typeof section === 'string') return section
  return readSectionValue(section, 'content')
}

const ARTICLE_SPLIT_PATTERN = /(?:^|\n)\s*(Article\s+(\d+)\s*:\s*[^\n]+)\s*\n?([\s\S]*?)(?=(?:\n\s*Article\s+\d+\s*:)|$)/gi
const GROUPED_ARTICLE_TITLE_PATTERN = /^articles\s+\d+\s+to\s+\d+/i
const GROUPED_ARTICLE_KEY_PATTERN = /^article-\d+-\d+$/i
const ARTICLE_HEADING_PATTERN = /^\s*article\s+\d+\s*:/i
const INSTITUTION_LINE_PATTERN = /\b(university|college|faculty|department|institute|school|mae fah luang|thailand|kingdom)\b|มหาวิทยาลัย|สถาบัน/i
const NAME_LINE_PATTERN = /^(\(name\)|name\b|ชื่อ\s*:?\s*|mr\.?|mrs\.?|ms\.?|miss\b|dr\.?|prof\.?|asst\.?\s*prof\.?|assoc\.?\s*prof\.?|assistant professor\b|associate professor\b|professor\b)/i
const POSITION_LINE_PATTERN = /^(\(position\)|position\b|ตำแหน่ง\s*:?\s*|president\b|dean\b|vice\b|director\b|rector\b|chair\b|coordinator\b|chancellor\b)/i
const DATE_LINE_PATTERN = /^(date\b|วันที่\s*:?\s*)/i
const SIGNATURE_PHRASE_PATTERN = /\b(signed\s+(for|on behalf)|for and on behalf|in witness whereof|signatories|signature block)\b|ส่วนลงนาม|ลายมือชื่อ|ผู้ลงนาม/i
const DOTTED_SIGNATURE_LINE_PATTERN = /[._-]{6,}/

export function getAgreementSectionTitle(section) {
  return readSectionValue(section, 'title') || readSectionValue(section, 'label')
}

export function stripArticleHeadingPrefix(value) {
  return String(value || '')
    .trim()
    .replace(/^article\s+\d+\s*:\s*/i, '')
    .replace(/^articles\s+\d+\s+to\s+\d+\s*:\s*/i, '')
    .trim()
}

function splitGroupedAgreementSection(section) {
  const key = readSectionValue(section, 'key')
  const title = getAgreementSectionTitle(section)
  const content = readSectionValue(section, 'content')
  const articleHeadingMatches = content.match(/\bArticle\s+\d+\s*:/gi) || []
  const looksGrouped = GROUPED_ARTICLE_TITLE_PATTERN.test(title)
    || GROUPED_ARTICLE_KEY_PATTERN.test(key)
    || ARTICLE_HEADING_PATTERN.test(content)

  if (articleHeadingMatches.length <= 1 || !looksGrouped) return [section]

  const parts = []
  let match = null
  ARTICLE_SPLIT_PATTERN.lastIndex = 0
  while ((match = ARTICLE_SPLIT_PATTERN.exec(content)) !== null) {
    const heading = String(match[1] || '').trim()
    const number = String(match[2] || '').trim()
    const body = String(match[3] || '').trim()
    if (!heading) continue
    parts.push({
      ...section,
      key: number ? `article-${number}` : section.key,
      title: heading,
      content: body
    })
  }
  ARTICLE_SPLIT_PATTERN.lastIndex = 0

  return parts.length > 1 ? parts : [section]
}

export function normalizeAgreementSections(items) {
  if (!Array.isArray(items)) return []
  return items
    .flatMap((item, index) => {
      const section = {
        ...(item && typeof item === 'object' ? item : {}),
        key: readSectionValue(item, 'key') || `section-${index + 1}`,
        title: getAgreementSectionTitle(item) || `Section ${index + 1}`,
        content: String(item && item.content ? item.content : '')
      }
      return splitGroupedAgreementSection(section)
    })
    .filter((item) => String(item && (item.title || item.content || '')).trim())
}

export function buildAgreementArticleChipLabel(section, index) {
  const safeIndex = Number.isInteger(index) ? index + 1 : 1
  const title = getAgreementSectionTitle(section)
  return title || `Section ${safeIndex}`
}

export function isCenteredAgreementSection(section, activityType) {
  const key = readSectionValue(section, 'key').toLowerCase()
  const title = getAgreementSectionTitle(section).toLowerCase()
  return key === 'title-block'
    || key === 'opening'
    || key === 'overview'
    || title === 'agreement'
    || title === 'opening'
    || title === 'purpose'
    || title === 'agreement purpose'
    || title === 'mou purpose'
}

export function isSignatureAgreementSection(section) {
  const key = readSectionValue(section, 'key').toLowerCase()
  const title = getAgreementSectionTitle(section).toLowerCase()
  return key.includes('signature')
    || title.includes('signature')
    || title.includes('signatories')
    || isLikelySignatureContent(readSectionContent(section))
}

function isLikelySignatureContent(value) {
  const content = String(value || '').trim()
  if (!content) return false

  const lines = content
    .replace(/\r\n/g, '\n')
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)

  if (!lines.length) return false

  const hasSignaturePhrase = SIGNATURE_PHRASE_PATTERN.test(content)
  const hasTwoColumnSeparator = lines.some((line) => line.includes('||'))
  const nameLineCount = lines.filter((line) => NAME_LINE_PATTERN.test(line)).length
  const positionLineCount = lines.filter((line) => POSITION_LINE_PATTERN.test(line)).length
  const dateLineCount = lines.filter((line) => DATE_LINE_PATTERN.test(line)).length
  const institutionLineCount = lines.filter((line) => INSTITUTION_LINE_PATTERN.test(line)).length
  const dottedLineCount = lines.filter((line) => DOTTED_SIGNATURE_LINE_PATTERN.test(line)).length
  const markerCount = nameLineCount + positionLineCount + dateLineCount

  if (hasTwoColumnSeparator && (hasSignaturePhrase || markerCount >= 1 || institutionLineCount >= 1)) return true
  if (hasSignaturePhrase && (markerCount >= 2 || dottedLineCount >= 2 || institutionLineCount >= 2)) return true
  if (institutionLineCount >= 2 && markerCount >= 2 && lines.length <= 18) return true
  return dottedLineCount >= 2 && nameLineCount >= 1 && positionLineCount >= 1
}

export function shouldHideAgreementSectionLabel(section) {
  const key = readSectionValue(section, 'key').toLowerCase()
  const title = getAgreementSectionTitle(section).toLowerCase()
  return key === 'title-block'
    || key === 'opening'
    || title === 'agreement'
    || title === 'opening'
    || isSignatureAgreementSection(section)
}

export function getAgreementSectionDisplayTitle(section) {
  if (shouldHideAgreementSectionLabel(section)) return ''
  return stripArticleHeadingPrefix(getAgreementSectionTitle(section))
}

export function buildAgreementSignatureRows(sectionOrContent) {
  const content = typeof sectionOrContent === 'string'
    ? sectionOrContent
    : String(sectionOrContent && sectionOrContent.content ? sectionOrContent.content : '')
  const lines = content
    .replace(/\r\n/g, '\n')
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)

  if (!lines.length) return []

  if (lines.some((line) => line.includes('||'))) {
    return lines.map((line) => {
      const [left, ...rightParts] = line.split('||')
      return {
        left: String(left || '').trim(),
        right: rightParts.join('||').trim()
      }
    }).filter((row) => row.left || row.right)
  }

  const rows = []
  for (let index = 0; index < lines.length; index += 2) {
    rows.push({
      left: lines[index] || '',
      right: lines[index + 1] || ''
    })
  }
  return rows.filter((row) => row.left || row.right)
}

export function buildAgreementSignatureColumns(sectionOrContent) {
  const rows = buildAgreementSignatureRows(sectionOrContent)
  return {
    left: rows.map((row) => row.left).filter(Boolean),
    right: rows.map((row) => row.right).filter(Boolean)
  }
}

function isLikelyNameLine(value) {
  const normalized = String(value || '').trim()
  if (!normalized) return false
  return NAME_LINE_PATTERN.test(normalized) || /,\s*(ph\.?d|m\.?d|d\.?phil)\b/i.test(normalized)
}

function isPlaceholderOnlyLine(value) {
  const normalized = String(value || '').trim()
  if (!normalized) return true
  const withoutLabel = normalized
    .replace(/^\(name\)\s*/i, '')
    .replace(/^name\s*:?\s*/i, '')
    .replace(/^ชื่อ\s*:?\s*/i, '')
    .replace(/^\(position\)\s*/i, '')
    .replace(/^position\s*:?\s*/i, '')
    .replace(/^ตำแหน่ง\s*:?\s*/i, '')
    .trim()
  return !withoutLabel || /^[.\-_ ]{6,}$/.test(withoutLabel)
}

function sanitizeSignatureValue(lines, labelPattern) {
  const normalizedLines = (Array.isArray(lines) ? lines : [])
    .map((line) => String(line || '').trim())
    .filter(Boolean)

  if (!normalizedLines.length) return ''

  const hasRealContent = normalizedLines.some((line) => !isPlaceholderOnlyLine(line))
  const cleaned = normalizedLines
    .filter((line) => hasRealContent ? !isPlaceholderOnlyLine(line) : false)
    .map((line) => labelPattern ? line.replace(labelPattern, '').trim() : line)
    .filter(Boolean)

  return cleaned.join('\n').trim()
}

function isLikelyHeaderContinuation(line, nextLine) {
  const normalized = String(line || '').trim()
  if (!normalized) return false
  if (INSTITUTION_LINE_PATTERN.test(normalized)) return true
  if (isLikelyNameLine(normalized) || POSITION_LINE_PATTERN.test(normalized) || DATE_LINE_PATTERN.test(normalized)) return false
  if (nextLine && isLikelyNameLine(nextLine)) return true
  return normalized.split(/\s+/).length <= 4
}

function buildAgreementSignatureColumnPreview(lines, fallbackHeader) {
  const normalizedLines = (Array.isArray(lines) ? lines : [])
    .map((line) => String(line || '').trim())
    .filter(Boolean)

  if (!normalizedLines.length) {
    return {
      header: String(fallbackHeader || '').trim(),
      name: '',
      position: '',
      date: 'Date:'
    }
  }

  const usedIndexes = new Set()
  const takeMatchingIndex = (pattern) => {
    const matchIndex = normalizedLines.findIndex((line, index) => !usedIndexes.has(index) && pattern.test(line))
    if (matchIndex >= 0) usedIndexes.add(matchIndex)
    return matchIndex
  }

  const dateIndex = takeMatchingIndex(DATE_LINE_PATTERN)
  const positionIndex = takeMatchingIndex(POSITION_LINE_PATTERN)
  const headerIndex = takeMatchingIndex(INSTITUTION_LINE_PATTERN)
  const fallbackHeaderValue = String(fallbackHeader || '').trim()
  const fallbackHeaderLines = fallbackHeaderValue
    .split('\n')
    .map((line) => line.trim().toLowerCase())
    .filter(Boolean)
  const fallbackHeaderSet = new Set(fallbackHeaderLines)
  const workingLines = normalizedLines
    .filter((line, index) => !usedIndexes.has(index))
    .filter((line) => !fallbackHeaderSet.has(String(line || '').trim().toLowerCase()))

  const contentHeaderLines = headerIndex >= 0 ? [normalizedLines[headerIndex]] : []

  while (workingLines.length && contentHeaderLines.length) {
    const candidate = workingLines[0]
    const nextCandidate = workingLines[1] || ''
    if (!isLikelyHeaderContinuation(candidate, nextCandidate)) break
    contentHeaderLines.push(workingLines.shift())
  }

  let header = fallbackHeaderValue || contentHeaderLines.join('\n').trim()
  if (!header && workingLines.length > 1 && !isLikelyNameLine(workingLines[0])) {
    header = workingLines.shift() || ''
  }

  const nameLines = []
  while (workingLines.length) {
    const candidate = workingLines.shift()
    if (!candidate) continue
    nameLines.push(candidate)
    if (!workingLines.length || !isLikelyNameLine(workingLines[0])) break
  }

  return {
    header,
    name: sanitizeSignatureValue(nameLines, /^\(name\)\s*|^name\s*:?\s*|^ชื่อ\s*:?\s*/i),
    position: sanitizeSignatureValue(positionIndex >= 0 ? [normalizedLines[positionIndex]] : [], /^\(position\)\s*|^position\s*:?\s*|^ตำแหน่ง\s*:?\s*/i),
    date: dateIndex >= 0 ? normalizedLines[dateIndex] : 'Date:'
  }
}

export function buildAgreementSignaturePreview(sectionOrContent, options = {}) {
  const columns = buildAgreementSignatureColumns(sectionOrContent)
  return {
    left: buildAgreementSignatureColumnPreview(columns.left, options.leftHeader || 'Mae Fah Luang University'),
    right: buildAgreementSignatureColumnPreview(columns.right, options.rightHeader || '')
  }
}
